/**
 * Pure rules behind the proposed-fix checklist: which ticks ask first, what
 * each row is called, the "who · when" stamp and the progress bar.
 *
 * @module components/regulation/finding/changes/FindingFixChecklist.logic
 */
import type { FixCheckConfirmation, FixReviewView, FixReviewWrite, TypedFixCheck } from "@repo/shared/findingChanges";

import type { OrganizationPerson } from "../../../../state/governance";
import { readableEmail } from "../../reviewConversationView";

/** Approvals and caveats go on the record with a name: ticking one asks first, unless ⌘/Ctrl-clicked. */
export const askBeforeTicking = (check: TypedFixCheck): boolean => check.kind === "approval" || check.kind === "caveat";

/**
 * The words of the dialog in front of an asking tick. An approval records an
 * approval whether or not it names its approver; only a caveat is "read".
 */
export function confirmCheckCopy(check: TypedFixCheck): { readonly title: string; readonly description: string; readonly action: string } {
  if (check.kind === "approval") {
    const approver = check.approver?.trim();
    return approver
      ? {
          title: `Record ${approver} approval?`,
          description: `This records that the ${approver} approved the amendment. Your name and the time go on the finding's record. Do this only when you have the approval.`,
          action: "Record approval",
        }
      : {
          title: "Record this approval?",
          description: `“${check.text}” This records that the approval was given. Your name and the time go on the finding's record. Do this only when you have the approval.`,
          action: "Record approval",
        };
  }
  return {
    title: "Acknowledge this caveat?",
    description: `“${check.text}” Your name and the time go on the finding's record as having read it.`,
    action: "Acknowledge",
  };
}

/**
 * What unticking a row writes. A check is done when it is confirmed or, for a
 * fill-in, when it is filled; unticking undoes both, or a filled and confirmed
 * fill-in would still read as done.
 */
export function untickWrites(
  check: TypedFixCheck,
  index: number,
  review: Pick<FixReviewView, "confirmations" | "fills"> | null,
): FixReviewWrite[] {
  const writes: FixReviewWrite[] = [];
  if (review?.confirmations[String(index)]) writes.push({ action: "confirm", index, confirmed: false });
  const key = check.kind === "fill" ? check.field?.key : undefined;
  if (key && review?.fills[key]?.trim()) writes.push({ action: "fill", key, value: "" });
  return writes;
}

/** The row's short label: the approver, the fill-in's field, or the check's own text. */
export function fixCheckLabel(check: TypedFixCheck): string {
  if (check.kind === "approval" && check.approver) return `Needs ${check.approver} approval`;
  if (check.kind === "fill" && check.field) return check.field.label;
  return check.text;
}

/**
 * The ticker by name: the server stamps whatever it knows (a name, or only
 * an email), so an email is matched to the organisation's people, else made
 * readable ("dev@flawstick.com" → "Dev").
 */
export function namedConfirmation(
  confirmation: FixCheckConfirmation,
  people: ReadonlyArray<OrganizationPerson>,
): FixCheckConfirmation {
  const by = confirmation.by?.trim();
  if (!by || !by.includes("@")) return confirmation;
  const person = people.find(
    (candidate) => candidate.userId === confirmation.byId || candidate.email.toLowerCase() === by.toLowerCase(),
  );
  return { ...confirmation, by: person?.name?.trim() || readableEmail(by) };
}

/** "Ann · 09:30" today, "Ann · 12 Oct" before; "Someone" when the account is gone. */
export function confirmationStamp(confirmation: FixCheckConfirmation, now: Date = new Date()): string {
  const who = confirmation.by?.trim().split(/\s+/)[0] || "Someone";
  const at = new Date(confirmation.at);
  if (Number.isNaN(at.getTime())) return who;
  const sameDay = at.toDateString() === now.toDateString();
  const when = sameDay
    ? at.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : at.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return `${who} · ${when}`;
}

/** The full line for the hover card: "Checked by Ann Admin · 16 Oct 2026, 09:30". */
export function confirmationDetail(confirmation: FixCheckConfirmation): string {
  const at = new Date(confirmation.at);
  const when = Number.isNaN(at.getTime())
    ? ""
    : ` · ${at.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}`;
  return `Checked by ${confirmation.by?.trim() || "a former member"}${when}`;
}

/** Width of the progress bar, 0–100. */
export const progressPercent = (done: number, total: number): number =>
  total <= 0 ? 0 : Math.round((Math.min(done, total) / total) * 100);
